import React, { useCallback, useMemo } from 'react';
import { Collapse, Paper, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import NotesListLabelGroupHeader, {
  useTimesheetLabels
} from 'modules/notes/list/groupBy/NotesListLabelGroupHeader';
import { TimeSignal } from 'graphql/types';
import { useTimeSignalExcludeAction } from 'modules/notes/notesSelector/hooks/useTimeSignalExcludeAction';
import {
  NOTE_LIST_ITEM_HEIGHT,
  NoteListItem
} from 'modules/notes/list/NoteListItem';
import { useSortedNotes } from 'modules/notes/list/hooks/useSortedNotes';
import { useNotesListItemDrop } from 'modules/notes/list/hooks/useNotesListItemDrop';
import { useUpdateTimeSignal } from '../../addNote/hooks';
import { useNotesToastContext } from '../../NotesToastContextProvider';
import { useNotesSortContext } from '../../NotesSortContextProvider';
import { useTimeSignalActions } from '../../notesSelector/hooks';
import { TimesheetLabel } from '../../../timesheet-labels/labelSchema';
import { useDialogState } from '../../../common/hooks';
import { TimesheetLabelSuggestionContextProvider } from '../../../timesheet-labels';
import { useNotesSelectionReset } from '../hooks';
import { EditNoteDialog } from '../../addNote';
import { useGroupPrimaryNote, isManualNote } from './hooks/useGroupPrimaryNote';
import { useGroupByScrollbarStyles } from './hooks/useGroupByScrollbarStyles';
import { GroupEditDialog } from './groupEdit';

interface NotesListLabelGroupProps {
  expanded: boolean;
  groupIndex: string;
  day: string;
  notes: TimeSignal[];
  totalDuration: number;
  totalCount: number;
  onExpandCollapse: (day: string) => void;
  isUncategorizedGroup: boolean;
}

interface UseStylesProps {
  isUncategorizedGroup: boolean;
  isDragHovering: boolean;
  isScrollRequired: boolean;
}

const MAX_VIEWABLE_UNCATEGORIZED_ITEMS = 3;

const useStyles = makeStyles((theme) => ({
  paper: ({ isUncategorizedGroup, isDragHovering }: UseStylesProps) => ({
    background: isDragHovering
      ? theme.palette.success.light
      : isUncategorizedGroup
      ? theme.palette.groupBy.uncategorizedBackground
      : theme.palette.groupBy.groupByBackground,
    border:
      isUncategorizedGroup && isDragHovering
        ? `${theme.palette.success.main} dashed ${theme.spacing(0.25)}`
        : isUncategorizedGroup
        ? `${
            theme.palette.groupBy.uncategorizedBorderColor
          } dashed ${theme.spacing(0.25)}`
        : undefined,
    borderRadius:
      isUncategorizedGroup || isDragHovering
        ? theme.spacing(0.5)
        : theme.spacing(0),
    margin: isUncategorizedGroup ? theme.spacing(2, 0.75) : undefined,
    padding: isUncategorizedGroup ? theme.spacing(2, 0) : undefined,
    position: 'relative'
  }),
  uncategorizedCount: {
    position: 'absolute',
    background: '#fff',
    zIndex: 2,
    left: theme.spacing(1),
    top: theme.spacing(-1.5),
    padding: theme.spacing(0, 1)
  },
  scrollContainer: ({ isScrollRequired }: UseStylesProps) => ({
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: theme.spacing(
      NOTE_LIST_ITEM_HEIGHT * MAX_VIEWABLE_UNCATEGORIZED_ITEMS
    ),
    margin: theme.spacing(0, isScrollRequired ? 1 : 0, 0, 0)
  }),
  border: ({ isUncategorizedGroup, isDragHovering }: UseStylesProps) => ({
    border:
      isDragHovering && !isUncategorizedGroup
        ? `${theme.palette.success.main} dashed ${theme.spacing(0.25)}`
        : undefined,
    borderRadius: theme.spacing(0.5),
    position: 'absolute',
    top: '0px',
    left: '0px',
    height: '100%',
    width: '100%',
    pointerEvents: 'none'
  })
}));

export const NotesListLabelGroup: React.FC<NotesListLabelGroupProps> = ({
  expanded,
  groupIndex,
  day,
  notes,
  totalCount,
  totalDuration,
  onExpandCollapse,
  isUncategorizedGroup
}: NotesListLabelGroupProps) => {
  const { handleTimeSignalAction, isActionInProgress } = useTimeSignalActions();
  const {
    handleTimeSignalExcludeAction,
    isActionInProgress: isExcludeActionInProgress
  } = useTimeSignalExcludeAction();
  const { sortOrder } = useNotesSortContext();
  const sortedNotes = useSortedNotes(notes, sortOrder);

  const timeoutProp = useMemo(() => {
    return { enter: 250, exit: 100 };
  }, []);

  const { setToastContext: showToastWithContext } = useNotesToastContext();
  const { onEditNote } = useUpdateTimeSignal({
    showToastWithContext: showToastWithContext
  });

  const labelsToUpdate = useTimesheetLabels(notes[0]);
  const { isOver, canDrop, dropRef } = useNotesListItemDrop({
    labelsToUpdate: isUncategorizedGroup
      ? labelsToUpdate.map((label: TimesheetLabel) => ({
          id: label.id,
          name: label.name,
          type: label.type
        }))
      : labelsToUpdate,
    onEditNote: onEditNote,
    groupIndex: groupIndex,
    day: day
  });
  const isScrollRequired = useMemo(
    () => notes.length > MAX_VIEWABLE_UNCATEGORIZED_ITEMS,
    [notes]
  );
  const classes = useStyles({
    isUncategorizedGroup: isUncategorizedGroup,
    isDragHovering: isOver && canDrop,
    isScrollRequired: isScrollRequired
  });
  const scrollbarClasses = useGroupByScrollbarStyles();

  const messageValues = useMemo(() => ({ count: notes.length }), [notes]);
  const { resetForm } = useNotesSelectionReset();
  const primaryNote = useGroupPrimaryNote(notes);

  const {
    open: isGroupEditDialogOpen,
    openDialog: openGroupEditDialog,
    closeDialog: closeGroupEditDialog
  } = useDialogState();
  const {
    open: isEditDialogOpen,
    openDialog: openEditDialog,
    closeDialog: closeEditDialog
  } = useDialogState();
  const onDialogOpen = useCallback(() => {
    if (primaryNote?.sources?.length === 1 && isManualNote(primaryNote)) {
      openEditDialog();
    } else {
      openGroupEditDialog();
    }
  }, [openEditDialog, openGroupEditDialog, primaryNote]);
  const onGroupEditDialogClose = useCallback(() => {
    closeGroupEditDialog();
    resetForm();
  }, [closeGroupEditDialog, resetForm]);
  const onEditDialogClose = useCallback(() => {
    closeEditDialog();
    resetForm();
  }, [closeEditDialog, resetForm]);

  return (
    <>
      <Paper
        elevation={0}
        className={classnames(classes.paper)}
        data-testid={`notes-list-label-group-${groupIndex}`}
        ref={dropRef}
        tabIndex={0}
      >
        <div className={classes.border}></div>
        {isUncategorizedGroup && (
          <Typography
            data-testid={`uncategorized-count-${groupIndex}`}
            variant="body2"
            className={classnames(classes.uncategorizedCount)}
          >
            <FormattedMessage
              id="note.groupBy.uncategorizedCountTitle"
              values={messageValues}
            />
          </Typography>
        )}
        <NotesListLabelGroupHeader
          groupKey={groupIndex}
          expanded={expanded}
          onExpandCollapse={onExpandCollapse}
          notes={notes}
          primaryNote={primaryNote}
          totalCount={totalCount}
          totalDuration={totalDuration}
          isUncategorizedGroup={isUncategorizedGroup}
          isDragHovering={isOver && canDrop}
          onDialogOpen={onDialogOpen}
        />

        <Collapse
          in={expanded}
          timeout={timeoutProp}
          unmountOnExit={false}
          mountOnEnter={true}
        >
          <div
            data-testid={'notes-list-label-group-items'}
            className={classnames(
              isUncategorizedGroup && classes.scrollContainer,
              isUncategorizedGroup && scrollbarClasses.scrollbar
            )}
          >
            {sortedNotes.map((note: TimeSignal) => (
              <NoteListItem
                key={note.id}
                note={note}
                isItemGrouped={true}
                isItemUncategorized={isUncategorizedGroup}
                isItemScrolled={isScrollRequired}
                handleTimeSignalAction={handleTimeSignalAction}
                isActionInProgress={isActionInProgress}
                handleTimeSignalExcludeAction={handleTimeSignalExcludeAction}
                isExcludeActionInProgress={isExcludeActionInProgress}
                groupIndex={groupIndex}
              />
            ))}
          </div>
        </Collapse>
      </Paper>
      {isEditDialogOpen && notes.length && (
        <TimesheetLabelSuggestionContextProvider>
          <EditNoteDialog
            open={isEditDialogOpen}
            onClose={onEditDialogClose}
            timeSignal={primaryNote}
          />
        </TimesheetLabelSuggestionContextProvider>
      )}
      {isGroupEditDialogOpen && notes.length && (
        <TimesheetLabelSuggestionContextProvider>
          <GroupEditDialog
            open={isGroupEditDialogOpen}
            onClose={onGroupEditDialogClose}
            notes={notes}
            primaryNote={primaryNote}
          />
        </TimesheetLabelSuggestionContextProvider>
      )}
    </>
  );
};
